import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiFilter } from "@result/rui/utils/api";

import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils";

export const getLactationPeriods = createAsyncThunk(
  "lactationPeriod/getAll",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const molzaResponse = await ApiClient.get(
        "/api/dog/mleko/molza/page",
        undefined,
        page,
        filter,
        sort
      );

      return molzaResponse.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createLactationPeriod = createAsyncThunk(
  "lactationPeriod/create",
  async ({ dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post("/api/dog/mleko/molza", dto);
      return response.data.output;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateLactationPeriod = createAsyncThunk(
  "lactationPeriod/update",
  async ({ lactationPeriodId, dto }, { rejectWithValue }) => {
    try {
      await ApiClient.put(`/api/dog/mleko/molza/${lactationPeriodId}`, dto);
      return dto; // PUT may not return data, return the updated DTO
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getLactationPeriodById = createAsyncThunk(
  "lactationPeriod/getById",
  async (lactationPeriodId, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `/api/dog/mleko/molza/${lactationPeriodId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Fetch latest lactation for an animal
export const fetchLatestLactationForAnimal = createAsyncThunk(
  "lactationPeriod/latestLactation",
  async ({ kotitevId }, { rejectWithValue }) => {
    if (!kotitevId) {
      throw new Error("kotitevId is required");
    }

    try {
      const lactationFilter = new ApiFilter().equal("kotitevId", kotitevId);
      const lactationResponse = await ApiClient.get(
        `/api/dog/mleko/molza/page`,
        null,
        null,
        lactationFilter,
        "sort=zacetekMolzeDatum,desc"
      );
      const lactation = lactationResponse.data?.content?.[0];

      if (!lactation) {
        return rejectWithValue("lactation.lambingRecordNotFound");
      }

      return lactation;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const lactationPeriodSlice = createSlice({
  name: "lactationPeriod",
  initialState: {
    page: {
      content: [],
      number: 0,
    },
    lactationPeriod: null,
    latestMolzaData: null,
    error: null,
    isLoading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getLactationPeriods.fulfilled, (state, action) => {
        state.isLoading = false;
        state.page = action.payload;
      })
      .addCase(getLactationPeriods.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getLactationPeriods.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createLactationPeriod.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lactationPeriod = action.payload;
      })
      .addCase(createLactationPeriod.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createLactationPeriod.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateLactationPeriod.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lactationPeriod = action.payload;
      })
      .addCase(updateLactationPeriod.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateLactationPeriod.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getLactationPeriodById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lactationPeriod = action.payload;
      })
      .addCase(getLactationPeriodById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getLactationPeriodById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLatestLactationForAnimal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.latestMolzaData = action.payload;
      })
      .addCase(fetchLatestLactationForAnimal.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchLatestLactationForAnimal.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      });
  },
});

export default lactationPeriodSlice.reducer;
